import { Outlet, useChildMatches, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { Check, LayoutGrid, ListTree, RefreshCw, ShieldCheck, Upload } from "lucide-react";
import { useState } from "react";
import { BaselineTree } from "@/components/scope/baseline-tree";
import { BaselineUploadModal } from "@/components/scope/baseline-upload-modal";
import { ScopeTiles } from "@/components/scope/scope-tiles";
import { StoryDetailSheet } from "@/components/scope/story-detail-sheet";
import { ErrorState } from "@/components/shared/error-state";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useBaseline, useConfirmBaseline } from "@/hooks/use-baseline";
import { useSolution } from "@/hooks/use-solutions";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const SKELETON_KEYS = ["a", "b", "c"] as const;

export function ScopePage() {
  const { solutionId } = useParams({ from: "/s/$solutionId" });
  const search = useSearch({ from: "/s/$solutionId/scope" });
  const navigate = useNavigate();
  const baseline = useBaseline(solutionId);
  const solution = useSolution(solutionId);
  const confirm = useConfirmBaseline(solutionId);

  const hasRows = (baseline.data?.length ?? 0) > 0;
  const isConfirmed = Boolean(solution.data?.baselineConfirmedAt);
  const showConfirmAction = hasRows && !isConfirmed;
  const showUploadAction = !hasRows && !baseline.isLoading;
  const showReplaceAction = hasRows && !isConfirmed;

  const [uploadOpen, setUploadOpen] = useState(false);
  const [replaceOpen, setReplaceOpen] = useState(false);

  const existingCounts = baseline.data
    ? {
        deliverables: baseline.data.length,
        epics: baseline.data.reduce((n, d) => n + d.epics.length, 0),
        stories: baseline.data.reduce(
          (n, d) => n + d.epics.reduce((m, e) => m + e.stories.length, 0),
          0,
        ),
      }
    : null;

  const childMatches = useChildMatches();
  const storyMatch = childMatches.find((m) => m.routeId.endsWith("/$storyId"));
  const selectedStoryId =
    storyMatch && "storyId" in storyMatch.params ? (storyMatch.params.storyId as string) : null;

  function setView(view: "list" | "tile") {
    navigate({
      to: "/s/$solutionId/scope",
      params: { solutionId },
      search: (prev) => ({ ...prev, view, d: undefined, e: undefined }),
    });
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 space-y-6">
      <PageHeader
        title="Scope"
        description={
          isConfirmed && solution.data?.baselineConfirmedAt
            ? `Confirmed ${formatDate(solution.data.baselineConfirmedAt)} · L1 → L2 → L3 baseline`
            : "L1 deliverables → L2 epics → L3 stories. Confirm the baseline below to unlock Process AI."
        }
        actions={
          <div className="inline-flex items-center gap-2">
            {showUploadAction && (
              <Button size="sm" onClick={() => setUploadOpen(true)}>
                <Upload className="h-3.5 w-3.5" />
                Upload baseline
              </Button>
            )}
            {showReplaceAction && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setReplaceOpen(true)}
                title="Replace this unconfirmed baseline with a corrected CSV"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Replace
              </Button>
            )}
            {showConfirmAction && (
              <Button
                size="sm"
                onClick={() => confirm.mutate()}
                disabled={confirm.isPending}
                title="Confirm this baseline so Process AI can compare new intake against it"
              >
                <Check className="h-3.5 w-3.5" />
                {confirm.isPending ? "Confirming…" : "Confirm baseline"}
              </Button>
            )}
            {isConfirmed && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                <ShieldCheck className="h-3.5 w-3.5" />
                Confirmed
              </span>
            )}
            <div className="inline-flex items-center rounded-md border border-[var(--color-border)] bg-card p-0.5 h-9">
              <button
                type="button"
                onClick={() => setView("list")}
                title="List view (tree)"
                className={cn(
                  "inline-flex h-8 px-3 items-center gap-1.5 rounded-[5px] text-xs font-medium transition-colors",
                  search.view === "list"
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <ListTree className="h-3.5 w-3.5" />
                List
              </button>
              <button
                type="button"
                onClick={() => setView("tile")}
                title="Tile view (drill-down)"
                className={cn(
                  "inline-flex h-8 px-3 items-center gap-1.5 rounded-[5px] text-xs font-medium transition-colors",
                  search.view === "tile"
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Tile
              </button>
            </div>
          </div>
        }
      />

      {baseline.isLoading && (
        <div className="space-y-3">
          {SKELETON_KEYS.map((k) => (
            <Skeleton key={k} className="h-24 rounded-lg" />
          ))}
        </div>
      )}

      {baseline.isError && (
        <ErrorState message="Failed to load baseline" onRetry={() => baseline.refetch()} />
      )}

      {baseline.data && baseline.data.length === 0 && (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] p-12 text-center space-y-4">
          <div>
            <h2 className="font-display text-lg font-semibold mb-1">No baseline yet</h2>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Upload a baseline CSV to define this solution's scope. Once confirmed, you can bring
              in meeting transcripts and intake docs to triage against it.
            </p>
          </div>
          <Button onClick={() => setUploadOpen(true)}>
            <Upload className="h-3.5 w-3.5" />
            Upload baseline CSV
          </Button>
        </div>
      )}

      {baseline.data && baseline.data.length > 0 && search.view === "list" && (
        <BaselineTree deliverables={baseline.data} selectedStoryId={selectedStoryId} />
      )}

      {baseline.data && baseline.data.length > 0 && search.view === "tile" && (
        <ScopeTiles
          deliverables={baseline.data}
          drillD={search.d ?? null}
          drillE={search.e ?? null}
        />
      )}

      <BaselineUploadModal
        solutionId={solutionId}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        mode="import"
      />
      <BaselineUploadModal
        solutionId={solutionId}
        open={replaceOpen}
        onOpenChange={setReplaceOpen}
        mode="replace"
        existingCounts={existingCounts}
      />

      <Outlet />
    </div>
  );
}

export function ScopeStoryPage() {
  return <StoryDetailSheet />;
}
